// Utilitaire pour gérer les connexions multiples côté client
export class MultiTabManager {
  private static readonly TAB_ID_KEY = 'quiz_tab_id';
  private static readonly ACTIVE_TABS_KEY = 'quiz_active_tabs';
  private static readonly TAB_HEARTBEAT_KEY = 'quiz_tab_heartbeat';
  
  private tabId: string;
  private heartbeatInterval: number | null = null;
  
  constructor() {
    this.tabId = this.generateTabId();
    this.initializeTab();
    this.startHeartbeat();
    this.cleanupOnUnload();
  }

  private generateTabId(): string {
    return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeTab(): void {
    // Enregistrer ce nouvel onglet
    const activeTabs = this.getActiveTabs();
    activeTabs[this.tabId] = Date.now();
    localStorage.setItem(MultiTabManager.ACTIVE_TABS_KEY, JSON.stringify(activeTabs));
    
    console.log(`🆔 Nouvel onglet initialisé: ${this.tabId}`);
    this.logActiveTabs();
  }

  private getActiveTabs(): { [tabId: string]: number } {
    try {
      const tabs = localStorage.getItem(MultiTabManager.ACTIVE_TABS_KEY);
      return tabs ? JSON.parse(tabs) : {};
    } catch (error) {
      console.error('Erreur lors de la lecture des onglets actifs:', error);
      return {};
    }
  }

  private startHeartbeat(): void {
    // Mettre à jour le heartbeat toutes les secondes
    this.heartbeatInterval = setInterval(() => {
      const activeTabs = this.getActiveTabs();
      activeTabs[this.tabId] = Date.now();
      localStorage.setItem(MultiTabManager.ACTIVE_TABS_KEY, JSON.stringify(activeTabs));
      
      // Nettoyer les onglets inactifs (plus de 5 secondes sans heartbeat)
      this.cleanupInactiveTabs();
    }, 1000);
  }

  private cleanupInactiveTabs(): void {
    const activeTabs = this.getActiveTabs();
    const now = Date.now();
    const threshold = 5000; // 5 secondes
    
    let hasChanges = false;
    Object.keys(activeTabs).forEach(tabId => {
      if (now - activeTabs[tabId] > threshold) {
        delete activeTabs[tabId];
        hasChanges = true;
        console.log(`🧹 Nettoyage de l'onglet inactif: ${tabId}`);
      }
    });

    if (hasChanges) {
      localStorage.setItem(MultiTabManager.ACTIVE_TABS_KEY, JSON.stringify(activeTabs));
    }
  }

  private cleanupOnUnload(): void {
    const cleanup = () => {
      // Supprimer cet onglet de la liste
      const activeTabs = this.getActiveTabs();
      delete activeTabs[this.tabId];
      localStorage.setItem(MultiTabManager.ACTIVE_TABS_KEY, JSON.stringify(activeTabs));
      console.log(`🗑️ Onglet ${this.tabId} supprimé lors de la fermeture`);
    };

    window.addEventListener('beforeunload', cleanup);
    window.addEventListener('unload', cleanup);
  }

  public getTabId(): string {
    return this.tabId;
  }

  public getActiveTabsCount(): number {
    const activeTabs = this.getActiveTabs();
    return Object.keys(activeTabs).length;
  }

  public isMultipleTabsOpen(): boolean {
    return this.getActiveTabsCount() > 1;
  }

  public getOtherActiveTabs(): string[] {
    const activeTabs = this.getActiveTabs();
    return Object.keys(activeTabs).filter(id => id !== this.tabId);
  }

  public logActiveTabs(): void {
    const activeTabs = this.getActiveTabs();
    console.log('📑 Onglets actifs:', Object.keys(activeTabs).length, activeTabs);
  }

  public destroy(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    // Supprimer cet onglet de la liste
    const activeTabs = this.getActiveTabs();
    delete activeTabs[this.tabId];
    localStorage.setItem(MultiTabManager.ACTIVE_TABS_KEY, JSON.stringify(activeTabs));
  }

  // Méthode statique pour détecter les onglets multiples sans créer d'instance
  public static hasMultipleTabs(): boolean {
    try {
      const tabs = localStorage.getItem(MultiTabManager.ACTIVE_TABS_KEY);
      const activeTabs = tabs ? JSON.parse(tabs) : {};
      const now = Date.now();
      const threshold = 5000; // 5 secondes
      
      // Compter les onglets actifs
      const activeCount = Object.keys(activeTabs).filter(tabId => 
        now - activeTabs[tabId] <= threshold
      ).length;
      
      return activeCount > 1;
    } catch (error) {
      console.error('Erreur lors de la vérification des onglets multiples:', error);
      return false;
    }
  }
}
